import { useMutation, useQuery } from '@tanstack/react-query';
import {
  applyCompanyCode,
  ApplyCompanyCodeParams,
  getCompanyCodes,
} from 'api/checkout';
import {
  deleteUser,
  getUser,
  getUsers,
  GetUsersResponse,
  SearchUsersParams,
  setTrialPeriod,
  suspendAccess,
} from 'api/users';
import { TrialPeriodParams } from 'types';
import { getUserActivity } from 'api/activity';
import { getAssignmentHistory } from 'api/stats';

const staleTime = 1000 * 30; /* 30 minutes */
// reuse the same query key for the same query
const coachesQuery = { query: { isCoach: true } };

export const useGetUsers = (body?: SearchUsersParams, enabled = true) => {
  return useQuery({
    queryKey: ['users', body],
    queryFn: () => getUsers(body),
    staleTime,
    enabled,
  });
};

export const useGetCoaches = () => {
  return useQuery({
    queryKey: ['users', coachesQuery],
    queryFn: () => getUsers(coachesQuery),
    select: (data: GetUsersResponse) => data.items,
    staleTime,
  });
};

export const useGetUser = (userId?: string) => {
  return useQuery({
    queryKey: ['users', userId],
    queryFn: () => getUser(userId!),
    enabled: !!userId,
    staleTime,
  });
};

export const useGetCoachUsers = (couchId: string) => {
  return useQuery({
    queryKey: ['users', couchId],
    queryFn: () => getUsers(),
    select: (data: GetUsersResponse) =>
      data.items.filter((user) => user.coachId === couchId),
    staleTime,
  });
};

export const useGetAssignmentHistory = (
  userId: string,
  role: 'coach' | 'therapist'
) => {
  return useQuery({
    queryKey: ['assignment-history', userId, role],
    queryFn: () => getAssignmentHistory(userId, role),
    staleTime,
  });
};

export const useUserActivity = (userId?: string, monthOffset: number = 1) => {
  return useQuery({
    queryKey: ['userActivity', userId, monthOffset],
    queryFn: () => getUserActivity(userId!, monthOffset),
    enabled: !!userId,
    staleTime,
    cacheTime: staleTime,
  });
};

export const useDeleteUserMutation = () => {
  return useMutation((id: string) => deleteUser(id));
};

export const useSetTrialPeriodMutation = () => {
  return useMutation((body: TrialPeriodParams) => setTrialPeriod(body));
};

export const useSuspendAccessMutation = () => {
  return useMutation((id: string) => suspendAccess(id));
};

export const useCompanyCodesQuery = (enabled = true) => {
  return useQuery({
    queryKey: ['companyCodes'],
    queryFn: getCompanyCodes,
    enabled,
    staleTime,
    cacheTime: staleTime,
  });
};

export const useApplyCompanyCodeMutation = () => {
  return useMutation((params: ApplyCompanyCodeParams) =>
    applyCompanyCode(params)
  );
};
